import { toast } from 'sonner';

// Where the share-preview microsite lives. Falls back to the on-app
// /api/share/video/{id} endpoint if a Vercel mirror isn't configured.
//
// Why a separate base? audivue.io's Cloudflare edge blocks Facebook/Instagram
// crawlers (403). The Vercel-hosted /share-microsite returns the same OG
// HTML on a domain that crawlers can fetch.
const RAW_BASE = (process.env.REACT_APP_SHARE_BASE || '').trim();
const SHARE_BASE = RAW_BASE.replace(/\/$/, ''); // trim trailing slash

// Build a URL-safe slug from a video title. Keeps Facebook/LinkedIn from
// returning a stale cached preview: every distinct title produces a distinct
// path component, so the crawler sees a brand-new URL and re-scrapes the
// current OG tags. Backend route accepts the slug as a trailing path param
// but ignores it for lookup (id is the source of truth).
function slugify(text, max = 60) {
  if (!text) return '';
  return String(text)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')          // strip accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')              // non-alnum → hyphen
    .replace(/^-+|-+$/g, '')                  // trim hyphens
    .slice(0, max)
    .replace(/-+$/g, '');                     // re-trim after slice
}

function shareUrlFor(video) {
  const videoId = typeof video === 'string' ? video : video?.id;
  if (!videoId) return '';
  const slug = typeof video === 'object'
    ? slugify(video?.title || video?.artist || '')
    : '';
  const suffix = slug ? `/${slug}` : '';

  // Cache-busting query string: Facebook/iMessage/WhatsApp cache previews
  // keyed by the FULL URL. Once they've scraped a URL once, they will
  // happily serve a stale preview for 7+ days no matter what we change
  // server-side. Appending a per-share unique token forces the crawler to
  // treat each share as a brand-new URL → it re-scrapes our current OG
  // tags → preview always shows the right thumbnail + title.
  // Backend ignores the query string (the route only cares about path).
  // Token format: minutes since epoch (changes per share without being
  // jarringly long; collision risk is negligible at our share volume).
  const cb = Math.floor(Date.now() / 60_000).toString(36);

  if (SHARE_BASE) {
    return `${SHARE_BASE}/v/${encodeURIComponent(videoId)}${suffix}?v=${cb}`;
  }
  // Use the short `/api/v/{id}` route so the URL pasted into Facebook /
  // iMessage / WhatsApp looks clean. Backend serves the same OG-tagged HTML
  // page from both `/api/share/video/{id}` (legacy) and `/api/v/{id}`.
  return `https://audivue.io/api/v/${encodeURIComponent(videoId)}${suffix}?v=${cb}`;
}

export const shareVideo = async (video) => {
  if (!video?.id) return;
  const url = shareUrlFor(video);
  const shareData = {
    title: video.title || 'Check out this video on AUDIVUE',
    text: `${video.title || 'AI Music Video'} by ${video.artist || 'an artist'} on AUDIVUE`,
    url,
  };

  // Native share sheet (iOS/Android)
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Fall through to dialog
    }
  }

  // Desktop (or any browser without navigator.share): pop the share dialog
  // so the user gets an explicit "Copy link" button + visible URL +
  // social quick-share buttons. The dialog is mounted globally in App.js
  // and listens on this event — keeping the share API call-site identical
  // for every consumer.
  try {
    window.dispatchEvent(
      new CustomEvent('audivue:open-share-dialog', { detail: { video, url } })
    );
    return;
  } catch (_e) {
    // Truly ancient browser — fall through to the legacy auto-copy path
    // so something still happens.
  }

  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  } catch {
    toast.error('Failed to copy link');
  }
};

// Dedicated "Copy Link" action for the desktop player controls.
//
// Why this exists separately from `shareVideo`:
//   • On desktop macOS Safari + desktop Chrome, `navigator.share` DOES
//     exist and opens the OS share sheet. macOS's share sheet has no
//     "Copy Link" entry, so users had no path to grab the URL — the
//     entire reason for this helper.
//   • Keeping a copy-only surface also lets the button skip the share
//     dialog roundtrip and drop the link on the clipboard in one click.
//
// Copy strategy:
//   1. Try `navigator.clipboard.writeText` — modern, works on all HTTPS
//      desktop browsers (Chrome/Safari/Firefox current versions).
//   2. If Clipboard API is missing or blocked (older Safari, restrictive
//      iframe, HTTP context, some enterprise policies), fall back to
//      `document.execCommand('copy')` via a hidden textarea. Officially
//      deprecated but the ONLY reliable fallback and still supported
//      everywhere for exactly this reason.
//   3. If both fail, toast an error so the user isn't left wondering
//      whether it worked.
//
// Returns a Promise<boolean> so callers can chain on success if needed.
export const copyShareLink = async (video) => {
  if (!video?.id) return false;
  const url = shareUrlFor(video);
  if (!url) return false;

  // Path 1: Async Clipboard API. Only available on secure contexts (HTTPS
  // or localhost); silently unavailable elsewhere — hence the try/catch.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Copied!');
      return true;
    } catch (_e) {
      // Fall through to execCommand — some Safari builds throw here even
      // over HTTPS when the tab wasn't the active foreground tab at the
      // exact moment the promise resolved.
    }
  }

  // Path 2: legacy execCommand('copy'). Requires the selection to be
  // inside a currently-focusable, non-readonly element on the page —
  // hence the hidden textarea approach (readonly + `contenteditable` at
  // once is what Safari expects here). iOS Safari specifically needs
  // the element to be non-hidden and the selection range to be set
  // programmatically before the copy command runs.
  try {
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.setAttribute('readonly', '');
    // Position off-screen but keep it in-flow so iOS Safari treats it as
    // focusable. `display:none` would break the fallback.
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    const prevActive = document.activeElement;
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, url.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_e) {
      ok = false;
    }
    document.body.removeChild(ta);
    // Restore focus so we don't yank it from whatever the user had active.
    try { prevActive && prevActive.focus && prevActive.focus(); } catch (_e) { /* focus restore is best-effort */ }
    if (ok) {
      toast.success('Copied!');
      return true;
    }
  } catch (_e) {
    // Truly ancient browser or DOM is broken — fall through to error toast.
  }

  toast.error('Copy failed — please copy the link manually');
  return false;
};

// Exported so callers (e.g. the share dialog) can prefill a text input
// with the same URL the copy button would produce.
export { shareUrlFor };
